import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import * as Sentry from "@sentry/browser";
import React, { useCallback, useState } from "react";
import { useAlert } from "react-alert";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { useHistory } from "react-router-dom";

import { fetchEventDefs } from "../actions/eventDefs";
import useAccountMetadata from "../hooks/useAccountMetadata";
import { useScriptTag } from "../hooks/useScriptTag";
import { isAccountInHIPAAMode, makeRequest } from "../util";
import { SaaSDestination } from "./destinations";
import { ModalField } from "./modals/ConfigureIntegrationModal";
import { LoadedDestinationInfo } from "../types/global";

// There's a react npm package for this which could be nice, but it requires at
// least version 17 of react.
// https://socket.dev/npm/package/@wistia/react-embeds
// Can't copy the wistia embed snippet directly because we have to do a special
// dance in order to load script tags on the page dynamically.
function VideoFindYourMeasurementID() {
  // These are the two script tags from the wistia embed snippet
  useScriptTag("https://fast.wistia.com/embed/medias/57ie0gp7km.jsonp");
  useScriptTag("https://fast.wistia.com/assets/external/E-v1.js");

  return (
    // This is the div tag copied directly from the wistia embed snippet
    <div
      dangerouslySetInnerHTML={{
        __html: `<div class="wistia_responsive_padding" style="padding:55.0% 0 0 0;position:relative;"><div class="wistia_responsive_wrapper" style="height:100%;left:0;position:absolute;top:0;width:100%;"><div class="wistia_embed wistia_async_57ie0gp7km videoFoam=true" style="height:100%;position:relative;width:100%"><div class="wistia_swatch" style="height:100%;left:0;opacity:0;overflow:hidden;position:absolute;top:0;transition:opacity 200ms;width:100%;"><img src="https://fast.wistia.com/embed/medias/57ie0gp7km/swatch" style="filter:blur(5px);height:100%;object-fit:contain;width:100%;" alt="" aria-hidden="true" onload="this.parentNode.style.opacity=1;" /></div></div></div></div>`,
      }}
    />
  );
}

interface DestinationConfigWizardProps {
  destination: SaaSDestination;
  isOpen: boolean;
  onClose: () => void;
}

export function DestinationConfigWizard({
  destination,
  isOpen,
  onClose,
}: DestinationConfigWizardProps) {
  const alert = useAlert();
  const accountMetadata = useAccountMetadata();
  const dispatch = useDispatch();
  const [measurementID, setMeasurementID] = useState("");
  const history = useHistory();

  const handleSave = useCallback(() => {
    // This is safe to generate here because it is only used for the intial
    // creation of the destination
    const id = uuidv4();
    const destConfig = {
      id: id,
      dest_type: destination.key,
      name: destination.key,
      connection_mode:
        isAccountInHIPAAMode(accountMetadata) && destination.supportsServerSide
          ? "server"
          : destination.connectionMode,
      settings: { measurementId: measurementID },
    };
    makeRequest("save_destination_config", destConfig)
      .then(() => {
        dispatch(fetchEventDefs());
        history.replace(destination.configPageUrl() + "/" + id);
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to save destination.");
        console.log(error);
        Sentry.captureException(error);
      });
  }, [accountMetadata, destination, measurementID]);

  if (!accountMetadata.loaded) {
    return null;
  }

  return (
    <Drawer
      anchor="bottom"
      variant="temporary"
      open={isOpen}
      onClose={onClose}
      PaperProps={{ square: false }}
    >
      <Box
        sx={{
          height: "85vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 3,
          }}
        >
          <Typography variant="h3">Set up Google Analytics 4</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 3,
            flex: 1,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4">Add your measurement ID</Typography>
            <br />
            <Typography variant="body1">
              In a second tab, navigate to Google Analytics and copy your
              measurement ID. Once you have your measurement ID, paste it into
              the field below and click Save.
            </Typography>
            <ModalField
              field={destination.configFields[0]}
              values={{ measurementId: measurementID }}
              settings={{}}
              handleFieldChange={(_, v) => setMeasurementID(v)}
            />
            <Button color="primary" onClick={handleSave} variant="contained">
              Save
            </Button>
          </Box>
          <Box sx={{ width: 10 }} />
          <Box sx={{ flex: 1 }}>
            <VideoFindYourMeasurementID />
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
